import React from 'react';
import { ChevronDown, Check, X, Send, Calendar } from 'lucide-react';
import './NewShiftPanel.css';

interface NewShiftWorker {
    readonly name: string;
    readonly initials: string;
    readonly avatarGradient: string;
    readonly role: string;
}

interface NewShiftPanelProps {
    readonly isVisible: boolean;
    readonly worker: NewShiftWorker;
    /** Auto-populated from the clicked cell — shown read-only */
    readonly dateRange: string;
    /** Whether the time dropdown is currently open */
    readonly isTimeDropdownOpen: boolean;
    /** Currently selected time value (empty = nothing selected yet) */
    readonly selectedTime: string;
    /** Whether the org dropdown is currently open */
    readonly isOrgDropdownOpen: boolean;
    /** Currently selected organisation (empty = nothing selected yet) */
    readonly selectedOrg: string;
    readonly typedInstructions: string;
    readonly isInstructionsFocused: boolean;
    readonly showSuccess: boolean;
}

const TIME_OPTIONS: readonly string[] = [
    '7AM – 3PM',
    '9AM – 5PM',
    '3PM – 11PM',
    '11PM – 7AM',
];

const ORG_OPTIONS: readonly string[] = [
    'Kingston Health SC',
    'Brampton Civic Hosp.',
    'Ottawa Hospital',
    'Hamilton Health',
];

/**
 * Floating panel for creating a new shift.
 * Always rendered (never unmounted) so getBoundingClientRect works
 * on all data-demo-target elements even when the panel is hidden.
 */
const NewShiftPanel = ({
    isVisible,
    worker,
    dateRange,
    isTimeDropdownOpen,
    selectedTime,
    isOrgDropdownOpen,
    selectedOrg,
    typedInstructions,
    isInstructionsFocused,
    showSuccess,
}: NewShiftPanelProps): React.ReactElement => (
    <div className={`nsp-panel ${isVisible ? 'nsp-panel--visible' : 'nsp-panel--hidden'}`}>

        {/* Header — worker + label */}
        <div className='nsp-header'>
            <div className='nsp-header-avatar' style={{ background: worker.avatarGradient }}>
                {worker.initials}
            </div>
            <div className='nsp-header-info'>
                <span className='nsp-header-title'>New Shift — {worker.name}</span>
                <span className='nsp-header-sub'>{worker.role}</span>
            </div>
            <span className='nsp-header-tag'>Draft</span>
        </div>

        <div className='nsp-body'>

            {/* Date — auto-populated, read-only */}
            <div className='nsp-field-group'>
                <div className='nsp-field-label'>Date</div>
                <div className='nsp-date-chip'>
                    <Calendar size={9} className='nsp-date-icon' />
                    {dateRange}
                </div>
            </div>

            {/* Time dropdown */}
            <div className='nsp-field-group'>
                <div className='nsp-field-label'>Time</div>
                <div className='nsp-dropdown'>
                    <div
                        data-demo-target='nsp-time-trigger'
                        className={`nsp-dropdown-trigger${isTimeDropdownOpen ? ' nsp-dropdown-trigger--open' : ''}${selectedTime ? ' nsp-dropdown-trigger--selected' : ''}`}
                    >
                        {selectedTime
                            ? <span>{selectedTime}</span>
                            : <span className='nsp-dropdown-placeholder'>Select shift time...</span>
                        }
                        <ChevronDown
                            size={10}
                            className={`nsp-dropdown-chevron${isTimeDropdownOpen ? ' nsp-dropdown-chevron--open' : ''}`}
                        />
                    </div>

                    {/* Options — always in DOM for getBoundingClientRect */}
                    <div className={`nsp-dropdown-list${isTimeDropdownOpen ? ' nsp-dropdown-list--open' : ''}`}>
                        {TIME_OPTIONS.map((opt: string) => (
                            <div
                                key={opt}
                                className={`nsp-dropdown-option${opt === '9AM – 5PM' ? ' nsp-dropdown-option--highlight' : ''}`}
                                data-demo-target={opt === '9AM – 5PM' ? 'nsp-time-opt-selected' : undefined}
                            >
                                {opt}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Organisation dropdown */}
            <div className='nsp-field-group'>
                <div className='nsp-field-label'>Organization</div>
                <div className='nsp-dropdown'>
                    <div
                        data-demo-target='nsp-org-trigger'
                        className={`nsp-dropdown-trigger${isOrgDropdownOpen ? ' nsp-dropdown-trigger--open' : ''}${selectedOrg ? ' nsp-dropdown-trigger--selected' : ''}`}
                    >
                        {selectedOrg
                            ? <span>{selectedOrg}</span>
                            : <span className='nsp-dropdown-placeholder'>Select organization...</span>
                        }
                        <ChevronDown
                            size={10}
                            className={`nsp-dropdown-chevron${isOrgDropdownOpen ? ' nsp-dropdown-chevron--open' : ''}`}
                        />
                    </div>

                    <div className={`nsp-dropdown-list${isOrgDropdownOpen ? ' nsp-dropdown-list--open' : ''}`}>
                        {ORG_OPTIONS.map((opt: string) => (
                            <div
                                key={opt}
                                className={`nsp-dropdown-option${opt === 'Kingston Health SC' ? ' nsp-dropdown-option--highlight' : ''}`}
                                data-demo-target={opt === 'Kingston Health SC' ? 'nsp-org-opt-selected' : undefined}
                            >
                                {opt}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Instructions */}
            <div className='nsp-field-group'>
                <div className='nsp-field-label'>Instructions</div>
                <div
                    data-demo-target='nsp-instructions'
                    className={`nsp-instructions-field${isInstructionsFocused ? ' nsp-instructions-field--focused' : ''}`}
                >
                    <span>{typedInstructions}</span>
                    {isInstructionsFocused && <span className='nsp-cursor-blink' />}
                </div>
            </div>

        </div>

        {/* Actions */}
        <div className='nsp-actions'>
            <button className='nsp-btn nsp-btn--cancel'>
                <X size={10} />
                Cancel
            </button>
            <button
                data-demo-target='nsp-send-btn'
                className='nsp-btn nsp-btn--send'
            >
                <Send size={10} />
                Send Shift
            </button>
        </div>

        {/* Success overlay */}
        <div className={`nsp-success${showSuccess ? ' nsp-success--visible' : ''}`}>
            <svg className='nsp-check-svg' viewBox='0 0 56 56'>
                {showSuccess && (
                    <>
                        <circle
                            className='nsp-check-bg'
                            cx='28' cy='28' r='26'
                            fill='#dcfce7'
                        />
                        <circle
                            className='nsp-check-ring'
                            cx='28' cy='28' r='26'
                            fill='none'
                            stroke='#16a34a'
                            strokeWidth='2.5'
                        />
                        <path
                            className='nsp-check-path'
                            d='M16 28l8 8 16-16'
                            fill='none'
                            stroke='#16a34a'
                            strokeWidth='3.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </>
                )}
            </svg>
            <span className='nsp-success-label'>Shift Sent!</span>
            <span className='nsp-success-sub'>{worker.name} has been notified</span>
        </div>

    </div>
);

export { NewShiftPanel };
export type { NewShiftWorker };
